import React, { useState, useEffect } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';
import './InstallPWA.css';

// Translations for install instructions
const translations = {
  en: {
    installApp: "Install app",
    installTitle: "Install the app",
    installTitleIOS: "Install on iPhone",
    followSteps: "Follow these steps:",
    step1iOS: 'Tap the <strong>Share</strong> button',
    step2iOS: 'Select <strong>"Add to Home Screen"</strong>',
    step3iOS: 'Tap <strong>Add</strong>',
    step1Desktop: 'Open the <strong>browser menu</strong> (⋮ or ⋯)',
    step2Desktop: 'Select <strong>"Install app"</strong> or <strong>"Add to Home Screen"</strong>',
    step3Desktop: 'Confirm the installation',
    understood: "Got it"
  },
  fr: {
    installApp: "Installer l'app",
    installTitle: "Installer l'application",
    installTitleIOS: "Installer sur iPhone",
    followSteps: "Suivez ces étapes :",
    step1iOS: 'Appuyez sur <strong>Partager</strong>',
    step2iOS: 'Sélectionnez <strong>"Sur l\'écran d\'accueil"</strong>',
    step3iOS: 'Appuyez sur <strong>Ajouter</strong>',
    step1Desktop: 'Ouvrez le <strong>menu du navigateur</strong> (⋮ ou ⋯)',
    step2Desktop: 'Sélectionnez <strong>"Installer l\'application"</strong> ou <strong>"Ajouter à l\'écran d\'accueil"</strong>',
    step3Desktop: 'Confirmez l\'installation',
    understood: "J'ai compris"
  },
  es: {
    installApp: "Instalar app",
    installTitle: "Instalar la aplicación",
    installTitleIOS: "Instalar en iPhone",
    followSteps: "Sigue estos pasos:",
    step1iOS: 'Toca el botón <strong>Compartir</strong>',
    step2iOS: 'Selecciona <strong>"Añadir a pantalla de inicio"</strong>',
    step3iOS: 'Toca <strong>Añadir</strong>',
    step1Desktop: 'Abre el <strong>menú del navegador</strong> (⋮ o ⋯)',
    step2Desktop: 'Selecciona <strong>"Instalar aplicación"</strong> o <strong>"Añadir a pantalla de inicio"</strong>',
    step3Desktop: 'Confirma la instalación',
    understood: "Entendido"
  },
  de: {
    installApp: "App installieren",
    installTitle: "App installieren",
    installTitleIOS: "Auf iPhone installieren",
    followSteps: "Folge diesen Schritten:",
    step1iOS: 'Tippe auf <strong>Teilen</strong>',
    step2iOS: 'Wähle <strong>"Zum Home-Bildschirm"</strong>',
    step3iOS: 'Tippe auf <strong>Hinzufügen</strong>',
    step1Desktop: 'Öffne das <strong>Browser-Menü</strong> (⋮ oder ⋯)',
    step2Desktop: 'Wähle <strong>"App installieren"</strong> oder <strong>"Zum Startbildschirm hinzufügen"</strong>',
    step3Desktop: 'Bestätige die Installation',
    understood: "Verstanden"
  },
  it: {
    installApp: "Installa app",
    installTitle: "Installa l'applicazione",
    installTitleIOS: "Installa su iPhone",
    followSteps: "Segui questi passaggi:",
    step1iOS: 'Tocca il pulsante <strong>Condividi</strong>',
    step2iOS: 'Seleziona <strong>"Aggiungi a Home"</strong>',
    step3iOS: 'Tocca <strong>Aggiungi</strong>',
    step1Desktop: 'Apri il <strong>menu del browser</strong> (⋮ o ⋯)',
    step2Desktop: 'Seleziona <strong>"Installa app"</strong> o <strong>"Aggiungi alla schermata Home"</strong>',
    step3Desktop: 'Conferma l\'installazione',
    understood: "Ho capito"
  },
  pt: {
    installApp: "Instalar app",
    installTitle: "Instalar o aplicativo",
    installTitleIOS: "Instalar no iPhone",
    followSteps: "Siga estes passos:",
    step1iOS: 'Toque em <strong>Compartilhar</strong>',
    step2iOS: 'Selecione <strong>"Adicionar à Tela de Início"</strong>',
    step3iOS: 'Toque em <strong>Adicionar</strong>',
    step1Desktop: 'Abra o <strong>menu do navegador</strong> (⋮ ou ⋯)',
    step2Desktop: 'Selecione <strong>"Instalar aplicativo"</strong> ou <strong>"Adicionar à tela inicial"</strong>',
    step3Desktop: 'Confirme a instalação',
    understood: "Entendi"
  },
  nl: {
    installApp: "App installeren",
    installTitle: "App installeren",
    installTitleIOS: "Installeren op iPhone",
    followSteps: "Volg deze stappen:",
    step1iOS: 'Tik op <strong>Delen</strong>',
    step2iOS: 'Selecteer <strong>"Zet op beginscherm"</strong>',
    step3iOS: 'Tik op <strong>Voeg toe</strong>',
    step1Desktop: 'Open het <strong>browsermenu</strong> (⋮ of ⋯)',
    step2Desktop: 'Selecteer <strong>"App installeren"</strong> of <strong>"Toevoegen aan startscherm"</strong>',
    step3Desktop: 'Bevestig de installatie',
    understood: "Begrepen"
  },
  ja: {
    installApp: "アプリをインストール",
    installTitle: "アプリをインストール",
    installTitleIOS: "iPhoneにインストール",
    followSteps: "以下の手順に従ってください：",
    step1iOS: '<strong>共有</strong>ボタンをタップ',
    step2iOS: '<strong>「ホーム画面に追加」</strong>を選択',
    step3iOS: '<strong>追加</strong>をタップ',
    step1Desktop: '<strong>ブラウザメニュー</strong>を開く (⋮ または ⋯)',
    step2Desktop: '<strong>「アプリをインストール」</strong>または<strong>「ホーム画面に追加」</strong>を選択',
    step3Desktop: 'インストールを確認',
    understood: "了解"
  }
};

// Get user's language
const getUserLanguage = () => {
  const lang = navigator.language || navigator.userLanguage || 'en';
  const shortLang = lang.split('-')[0].toLowerCase();
  return translations[shortLang] ? shortLang : 'en';
};

const InstallPWA = () => {
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [showDesktopInstructions, setShowDesktopInstructions] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [lang, setLang] = useState('en');

  useEffect(() => {
    // Detect language
    setLang(getUserLanguage());

    // Check if already installed
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    
    if (isStandalone) {
      setIsInstalled(true);
      return;
    }

    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // For Android/Desktop, listen for beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const t = translations[lang];

  const handleInstallClick = async () => {
    if (isIOS) {
      setShowIOSInstructions(true);
    } else if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      setShowDesktopInstructions(true);
    }
  };

  if (isInstalled) return null;

  return (
    <>
      <button 
        className="install-pwa-button"
        onClick={handleInstallClick}
        data-testid="install-pwa-button"
        title={t.installTitle}
      >
        <Download size={20} strokeWidth={2} />
        <span>{t.installApp}</span>
      </button>

      {/* iOS Instructions Modal */}
      {showIOSInstructions && (
        <div className="ios-modal-overlay" onClick={() => setShowIOSInstructions(false)}>
          <div className="ios-modal" onClick={e => e.stopPropagation()}>
            <button 
              className="ios-modal-close" 
              onClick={() => setShowIOSInstructions(false)}
            >
              <X size={24} />
            </button>
            
            <h2>{t.installTitleIOS}</h2>
            <p>{t.followSteps}</p>
            
            <div className="ios-steps">
              <div className="ios-step">
                <div className="ios-step-number">1</div>
                <div className="ios-step-content">
                  <div className="ios-step-icon">
                    <Share size={24} />
                  </div>
                  <span dangerouslySetInnerHTML={{ __html: t.step1iOS }} />
                </div>
              </div>
              
              <div className="ios-step">
                <div className="ios-step-number">2</div>
                <div className="ios-step-content">
                  <div className="ios-step-icon add-icon">
                    <Plus size={20} />
                  </div>
                  <span dangerouslySetInnerHTML={{ __html: t.step2iOS }} />
                </div>
              </div>
              
              <div className="ios-step">
                <div className="ios-step-number">3</div>
                <div className="ios-step-content">
                  <div className="ios-step-icon confirm-icon">
                    +
                  </div>
                  <span dangerouslySetInnerHTML={{ __html: t.step3iOS }} />
                </div>
              </div>
            </div>
            
            <button 
              className="ios-modal-ok"
              onClick={() => setShowIOSInstructions(false)}
            >
              {t.understood}
            </button>
          </div>
        </div>
      )}

      {/* Desktop/Android Instructions Modal */}
      {showDesktopInstructions && (
        <div className="ios-modal-overlay" onClick={() => setShowDesktopInstructions(false)}>
          <div className="ios-modal" onClick={e => e.stopPropagation()}>
            <button 
              className="ios-modal-close" 
              onClick={() => setShowDesktopInstructions(false)}
            >
              <X size={24} />
            </button>
            
            <h2>{t.installTitle}</h2>
            <p>{t.followSteps}</p>
            
            <div className="ios-steps">
              <div className="ios-step">
                <div className="ios-step-number">1</div>
                <div className="ios-step-content">
                  <span dangerouslySetInnerHTML={{ __html: t.step1Desktop }} />
                </div>
              </div>
              
              <div className="ios-step">
                <div className="ios-step-number">2</div>
                <div className="ios-step-content">
                  <span dangerouslySetInnerHTML={{ __html: t.step2Desktop }} />
                </div>
              </div>
              
              <div className="ios-step">
                <div className="ios-step-number">3</div>
                <div className="ios-step-content">
                  <span dangerouslySetInnerHTML={{ __html: t.step3Desktop }} />
                </div>
              </div>
            </div>
            
            <button 
              className="ios-modal-ok"
              onClick={() => setShowDesktopInstructions(false)}
            >
              {t.understood}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPWA;
